import {
  getRelatedFederalContent,
  getResearchSourcesForJurisdiction,
  getStateCaselawSources,
  getStateLrrSources,
  isRelatedFederalContent,
} from '@/jurisdiction-mappings/jurisdiction-maps'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MAX_SELECTED_STATES } from '../constants'

export type ConversationResearchFilters = {
  selectedStateNames: string[]
  stateLrrSources: string[]
  stateCaselawSources: string[]
  federalLrrSources: string[]
  federalCaselawSources: string[]
}

type ResearchFiltersState = {
  filters: { [conversationId: string]: ConversationResearchFilters }
}

const createInitialConversationFilters = (): ConversationResearchFilters => ({
  selectedStateNames: [],
  stateLrrSources: [],
  stateCaselawSources: [],
  federalLrrSources: [],
  federalCaselawSources: [],
})

export function conversationHasResearchFilters(filters: ConversationResearchFilters | undefined): boolean {
  if (!filters) return false
  return (
    filters.selectedStateNames.length > 0 ||
    filters.stateLrrSources.length > 0 ||
    filters.stateCaselawSources.length > 0 ||
    filters.federalLrrSources.length > 0 ||
    filters.federalCaselawSources.length > 0
  )
}

/**
 * Combines LRR and caselaw sources from all selected states into deduplicated arrays.
 */
export function aggregateStateSources(stateNames: string[]): { lrr: string[]; caselaw: string[] } {
  const lrr = new Set<string>()
  const caselaw = new Set<string>()
  for (const name of stateNames) {
    for (const code of getStateLrrSources(name)) lrr.add(code)
    for (const code of getStateCaselawSources(name)) caselaw.add(code)
  }
  return { lrr: Array.from(lrr), caselaw: Array.from(caselaw) }
}

/**
 * Combines related federal content from all selected states into deduplicated arrays.
 */
export function aggregateRelatedFederalContent(stateNames: string[]): { lrrSources: string[]; courts: string[] } {
  const lrr = new Set<string>()
  const courts = new Set<string>()
  for (const name of stateNames) {
    const content = getRelatedFederalContent(name)
    for (const code of content.lrrSources) lrr.add(code)
    for (const code of content.courts) courts.add(code)
  }
  return { lrrSources: Array.from(lrr), courts: Array.from(courts) }
}

const initialState: ResearchFiltersState = {
  filters: {},
}

/**
 * Helper to ensure a conversation's filters exist in state
 */
const ensureConversationFilters = (state: ResearchFiltersState, conversationId: string): ConversationResearchFilters => {
  if (!state.filters[conversationId]) {
    state.filters[conversationId] = createInitialConversationFilters()
  }
  return state.filters[conversationId]
}

/**
 * Research Filters Slice
 * Manages the UI state for the sources dialog, separated into four arrays:
 * - stateLrrSources / stateCaselawSources (from state selection)
 * - federalLrrSources / federalCaselawSources (from "Include related federal content" + manual selections)
 */
const researchFiltersSlice = createSlice({
  name: 'researchFilters',
  initialState,
  reducers: {
    /**
     * Toggle a state on/off with a maximum of MAX_SELECTED_STATES.
     * Only updates the selected names array; aggregation and federal recomputation
     * are handled by the researchFiltersListenerMiddleware.
     */
    toggleStateSelected: (state, action: PayloadAction<{ conversationId: string; stateName: string }>) => {
      const { conversationId, stateName } = action.payload
      const filters = ensureConversationFilters(state, conversationId)

      const idx = filters.selectedStateNames.indexOf(stateName)
      if (idx >= 0) {
        filters.selectedStateNames.splice(idx, 1)
      } else {
        if (filters.selectedStateNames.length >= MAX_SELECTED_STATES) return
        filters.selectedStateNames.push(stateName)
      }
    },

    /**
     * Apply the side effects of a state toggle: updated source arrays computed by the listener.
     */
    applyStateToggleSideEffects: (
      state,
      action: PayloadAction<{
        conversationId: string
        stateLrrSources: string[]
        stateCaselawSources: string[]
        federalLrrSources: string[]
        federalCaselawSources: string[]
      }>
    ) => {
      const { conversationId, stateLrrSources, stateCaselawSources, federalLrrSources, federalCaselawSources } = action.payload
      const filters = ensureConversationFilters(state, conversationId)
      filters.stateLrrSources = stateLrrSources
      filters.stateCaselawSources = stateCaselawSources
      filters.federalLrrSources = federalLrrSources
      filters.federalCaselawSources = federalCaselawSources
    },

    /**
     * Toggle "Include related federal content"
     * When enabled: adds federal sources from mappings for all selected states
     * When disabled: derives which sources are related and removes them, preserving manual selections
     */
    toggleRelatedFederalContent: (state, action: PayloadAction<{ conversationId: string; enabled: boolean }>) => {
      const { conversationId, enabled } = action.payload
      const filters = ensureConversationFilters(state, conversationId)

      if (filters.selectedStateNames.length === 0) {
        return
      }

      if (enabled) {
        const { lrrSources, courts } = aggregateRelatedFederalContent(filters.selectedStateNames)
        filters.federalLrrSources = Array.from(new Set([...filters.federalLrrSources, ...lrrSources]))
        filters.federalCaselawSources = Array.from(new Set([...filters.federalCaselawSources, ...courts]))
      } else {
        filters.federalLrrSources = filters.federalLrrSources.filter(
          (code) => !filters.selectedStateNames.some((name) => isRelatedFederalContent(code, name).isRelatedLrr)
        )
        filters.federalCaselawSources = filters.federalCaselawSources.filter(
          (code) => !filters.selectedStateNames.some((name) => isRelatedFederalContent(code, name).isRelatedCaselaw)
        )
      }
    },

    /**
     * Add manually selected federal LRR sources
     */
    addFederalLrrSources: (state, action: PayloadAction<{ conversationId: string; sources: string[] }>) => {
      const { conversationId, sources } = action.payload
      const filters = ensureConversationFilters(state, conversationId)
      filters.federalLrrSources = Array.from(new Set([...filters.federalLrrSources, ...sources]))
    },

    /**
     * Remove manually selected federal LRR sources
     */
    removeFederalLrrSources: (state, action: PayloadAction<{ conversationId: string; sources: string[] }>) => {
      const { conversationId, sources } = action.payload
      const filters = ensureConversationFilters(state, conversationId)
      filters.federalLrrSources = filters.federalLrrSources.filter((source) => !sources.includes(source))
    },

    /**
     * Add manually selected federal caselaw sources (courts)
     */
    addFederalCaselawSources: (state, action: PayloadAction<{ conversationId: string; sources: string[] }>) => {
      const { conversationId, sources } = action.payload
      const filters = ensureConversationFilters(state, conversationId)
      filters.federalCaselawSources = Array.from(new Set([...filters.federalCaselawSources, ...sources]))
    },

    /**
     * Remove manually selected federal caselaw sources (courts)
     */
    removeFederalCaselawSources: (state, action: PayloadAction<{ conversationId: string; sources: string[] }>) => {
      const { conversationId, sources } = action.payload
      const filters = ensureConversationFilters(state, conversationId)
      filters.federalCaselawSources = filters.federalCaselawSources.filter((source) => !sources.includes(source))
      if (filters.federalCaselawSources.length === 0) {
        filters.federalLrrSources = []
      }
    },

    /**
     * Clear all sources for a conversation
     */
    clearAllSources: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      state.filters[conversationId] = createInitialConversationFilters()
    },

    /**
     * Prefill research filters from a matter jurisdiction when none are set yet.
     */
    prefillFromJurisdiction: (state, action: PayloadAction<{ conversationId: string; jurisdiction: string }>) => {
      const { conversationId, jurisdiction } = action.payload
      const filters = ensureConversationFilters(state, conversationId)

      if (conversationHasResearchFilters(filters)) {
        return
      }

      Object.assign(filters, getResearchSourcesForJurisdiction(jurisdiction))
    },
  },
})

export const researchFiltersActions = researchFiltersSlice.actions

export const researchFiltersReducer = researchFiltersSlice.reducer
